/**
 * Mount de la galería `/reportes.html` — Fase 5f.
 *
 * Patrón calcado de `briefings-list` y `debriefings-list`:
 *  1. mountChrome con activeSection='reportes'.
 *  2. Inyecta el shell de la galería en `chrome-main`. El shell decide
 *     si pintar el botón "+ Subir reporte" según el rol del user.
 *  3. Carga `GET /api/mission-reports/` y pinta una fila por reporte.
 *  4. Filas clicables — click/Enter/Espacio navegan a
 *     `/reportes/subir.html?id=<reportNumber>` (modo edit del form de
 *     subida, mismo patrón que briefings/debriefings). El botón X
 *     (si visible) intercepta el evento y abre el modal de borrado.
 *
 * Reutiliza `briefings-list.css` para mantener una estética unificada
 * con las otras dos galerías.
 */

import type { AuthenticatedPilot } from '@51esc/shared';
import { mountChrome } from '../chrome/index.js';
import { openConfirmModal } from '../confirm-modal/index.js';
import { deleteMissionReport, fetchMissionReports, type MissionReportRow } from './api.js';
import { renderEmptyRow, renderErrorRow, renderGalleryShell, renderRow } from './markup.js';

import '../briefings-list/briefings-list.css';
import './reportes-list.css';

/** Roles autorizados a subir reportes (espejo de `requireRole('officer')`). */
const OFFICER_ROLES: ReadonlyArray<AuthenticatedPilot['role']> = ['officer', 'admin', 'superadmin'];

export async function mountReportesList(): Promise<void> {
  const { contentRoot, user } = await mountChrome({ activeSection: 'reportes' });
  const canUpload = OFFICER_ROLES.includes(user.role);
  contentRoot.innerHTML = renderGalleryShell({ canUpload });

  const tbody = contentRoot.querySelector<HTMLElement>('[data-rgallery-rows]');
  if (!tbody) return;

  await reload(tbody, user, canUpload);

  // Delegación: click en X borra; click en cualquier otra parte de la
  // fila navega al detalle. Enter/Espacio teclados también navegan.
  tbody.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    const deleteBtn = target.closest<HTMLElement>('[data-rgallery-delete]');
    if (deleteBtn) {
      ev.stopPropagation();
      const reportNumber = deleteBtn.dataset['rgalleryDelete'];
      if (reportNumber) {
        void onDeleteClick(reportNumber, tbody, user, canUpload);
      }
      return;
    }
    const tr = target.closest<HTMLElement>('[data-rgallery-id]');
    if (!tr) return;
    navigateToDetail(tr.dataset['rgalleryId']);
  });
  tbody.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter' && ev.key !== ' ') return;
    const tr = (ev.target as HTMLElement).closest<HTMLElement>('[data-rgallery-id]');
    if (!tr) return;
    ev.preventDefault();
    navigateToDetail(tr.dataset['rgalleryId']);
  });
}

async function reload(
  tbody: HTMLElement,
  user: AuthenticatedPilot,
  canUpload: boolean,
): Promise<void> {
  tbody.innerHTML = `<tr><td colspan="10" class="bgallery-loading">cargando…</td></tr>`;
  const result = await fetchMissionReports();
  if (!result.ok) {
    tbody.innerHTML = renderErrorRow(result.message);
    return;
  }
  const list: MissionReportRow[] = result.data;
  if (list.length === 0) {
    tbody.innerHTML = renderEmptyRow({ canUpload });
    return;
  }
  tbody.innerHTML = list.map((r) => renderRow(r, user)).join('');
}

async function onDeleteClick(
  reportNumber: string,
  tbody: HTMLElement,
  user: AuthenticatedPilot,
  canUpload: boolean,
): Promise<void> {
  const confirmed = await openConfirmModal({
    title: 'Borrar reporte',
    message:
      `¿Seguro que quieres borrar el reporte ${reportNumber}? ` +
      `Se eliminarán también sus archivos, stats por piloto y eventos. ` +
      `Esta acción no se puede deshacer.`,
    confirmLabel: 'Borrar',
    danger: true,
  });
  if (!confirmed) return;

  const res = await deleteMissionReport(reportNumber);
  if (!res.ok) {
    await openConfirmModal({
      title: 'Error al borrar',
      message: res.message,
      confirmLabel: 'Cerrar',
      cancelLabel: 'Cerrar',
    });
    return;
  }
  await reload(tbody, user, canUpload);
}

function navigateToDetail(reportNumber: string | undefined): void {
  if (!reportNumber) return;
  window.location.href = `/reportes/subir.html?id=${encodeURIComponent(reportNumber)}`;
}
